import React, { FunctionComponent, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faPlus } from '@fortawesome/free-solid-svg-icons'
import Grid from '@mui/material/Grid'
import styled from 'styled-components'
import { observer } from 'mobx-react-lite'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import { colors, spacing } from '../../styles/_var'
import { AddContestantButton } from './CompetitionDashboard'
import Text from '../../components/Text'
import competitionDashboardStore from '../../stores/competitionDashboardStore'
import Button from '../../components/Button'
import Modal from '../../components/Modal'
import Input from '../../components/Input'
import uiStore from '../../stores/uiStore'
import ToggleSwitch from '../../components/ToggleSwitch'

interface ICompetitionCategories {}

const CompetitionCategories: FunctionComponent<ICompetitionCategories> = () => {
  const [showCategoryForm, setShowCategoryForm] = useState(false)
  const [editID, setEditID] = useState<null | number>(null)
  const [categoryName, setCategoryName] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const categories = competitionDashboardStore.allCategories
  const noCategories = !categories?.length

  const submitForm = () => {
    if (!categoryName.trim()) return setErrorMessage('This field is required')

    if (editID) {
      competitionDashboardStore.updateCategory(editID, categoryName, isDefault)
    } else {
      competitionDashboardStore.createCategory(categoryName, isDefault)
    }

    setCategoryName('')
    setShowCategoryForm(false)
  }

  const deleteCategory = (categoryId: number) => () => {
    uiStore.openConfirmDialog('Make sure this category has no contestants before deleting it. Are you sure you want to continue?', () => {
      competitionDashboardStore.deleteCategory(categoryId)
    })
  }

  const closeForm = () => {
    setEditID(null)
    setCategoryName('')
    setShowCategoryForm(false)
  }

  const updateCategory = (id: number, name: string, isDefault=false) => () => {
    setEditID(id)
    setCategoryName(name)
    setIsDefault(isDefault)
    setShowCategoryForm(true)
  }

  const onInputKeyPress = (e: any) => {
    const keyCode = e.which || e.keyCode || e.charCode
    const key = e.code || e.key 

    if (keyCode === 13 || key === 'Enter') submitForm()
  }


  return (
    <>
      <Modal onClose={closeForm} showModal={showCategoryForm}>
        <Box textAlign='center'>
          <Text variant='medium' weight="semi-bold" mb={1} lighten>
            {!!editID ? "Update" : "Create"} New Category
          </Text>
          <Input 
            onKeyDown={onInputKeyPress}
            onFocus={() => setErrorMessage('')}
            errorMessage={errorMessage}
            placeholder='Enter category name'
            value={categoryName}
            onChange={e => setCategoryName(e.target.value)}
          />
          <Box mt={2} display="flex" alignItems="center" gap={2} justifyContent="flex-start">
            <ToggleSwitch optionLabels={["Yes", "No"]} id="is-default" checked={isDefault} onChange={setIsDefault} />
            <Text variant='regular' lighten weight='semi-bold'>Is Default</Text>
          </Box>
          <Button mt={2} onClick={submitForm}>
            {!!editID ? 'Update' : 'Create'} Category
          </Button>
        </Box>
      </Modal>
      <CategoriesWrapper>
        <AddContestantButton
          mb={3}
          variant="secondary"
          onClick={() => setShowCategoryForm(true)}>
          <FontAwesomeIcon icon={faPlus} />
          <Text variant="regular">Add New Category</Text>
        </AddContestantButton>
        {noCategories && <Text variant='regular' center color={colors.red}>You haven't created any categories yet</Text>}
        {!noCategories && (
          <Grid container spacing={2}>
            {categories.map(({ id, name, is_default }) => (
              <Grid item xs={12} sm={6} md={4}  key={id}>
                <Paper style={{padding: spacing.sm, textAlign: 'center'}} elevation={5}>
                  <Box display='flex' justifyContent='center' alignItems='center' gap={1}>
                    {is_default && <FontAwesomeIcon icon={faCheckCircle} size="2x" color={colors.purple} />}
                    <Text variant='medium' center>{name}</Text>
                  </Box>
                  {!!id && (
                    <Box display='flex' justifyContent='space-between'>
                      <Button variant='secondary' onClick={updateCategory(id, name, is_default)}>
                        Edit
                      </Button>
                      <Button variant='danger' onClick={deleteCategory(id)}>
                        Delete
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </CategoriesWrapper>
    </>
  )
}

export default observer(CompetitionCategories)

const CategoriesWrapper = styled.div``
